import { DESKTOP_MIN_WIDTH, RESET_BACKDROP_STORE } from 'constants/common';
import { FX_ADD_TRANSACTION_ADDRESS_FINDER_NAME } from 'constants/fx';
import { Fade } from 'helpers/animation';
import { createLatestRequestTracker } from 'helpers/latestRequest';
import useFxAddTransaction from 'hooks/fx/useFxAddTransaction';
import useStore from 'hooks/useStore';
import { isEmpty } from 'lodash';
import { useRef, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { SET_SHOW_BACKDROP } from 'store/action';
import styled from 'styled-components';
import FormInputAddressFinderValidation from '../FormInputAddressFinderValidation';

const CustomAddressFinderDropdown = ({
  field,
  setPickupLocationDataByAusPost,
  setError,
}) => {
  const { dispatch } = useStore();

  const [addressFinder, setAddressFinder] = useState(null);
  const [addressSuggestion, setAddressSuggestion] = useState(null);

  const { setPickupLocationViaAusPost } = useFxAddTransaction();

  // Each suggestion pick takes a ticket; typing in the finder invalidates it,
  // so a late location reply cannot overwrite the pickup data or error that
  // typing or the clear icon just reset.
  const pickupRequestRef = useRef(createLatestRequestTracker());

  const handleDropdownItemSelected = (e, address) => {
    e.preventDefault();

    const { fullAddress, postcode } = address;

    const ticket = pickupRequestRef.current.begin();
    const isCurrent = () => pickupRequestRef.current.isLatest(ticket);

    setPickupLocationViaAusPost({
      postcode,
      setAusPost: (ausPost) => {
        if (isCurrent()) setPickupLocationDataByAusPost(ausPost);
      },
      setError: (message) => {
        if (isCurrent()) setError(message);
      },
    });

    document.getElementsByName(
      FX_ADD_TRANSACTION_ADDRESS_FINDER_NAME
    )[0].value = fullAddress;

    setAddressFinder(address);

    setAddressSuggestion(null);

    dispatch({
      type: SET_SHOW_BACKDROP,
      payload: RESET_BACKDROP_STORE,
    });
  };

  const renderAddressSuggestionDropdown = () => {
    if (isEmpty(addressSuggestion)) return null;
    return (
      <DropdownMenu show={true}>
        {addressSuggestion?.map((address, index) => (
          <DropdownItem
            key={address.id}
            eventKey={index + 1}
            onClick={(e) => handleDropdownItemSelected(e, address)}
          >
            {address.fullAddress}
          </DropdownItem>
        ))}
      </DropdownMenu>
    );
  };

  return (
    <CustomAddressFinderDropdownStyled>
      <FormInputAddressFinderValidation
        field={field}
        setAddressSuggestion={setAddressSuggestion}
        addressFinder={addressFinder}
        setAddressFinder={setAddressFinder}
        setPickupLocationDataByAusPost={setPickupLocationDataByAusPost}
        setError={setError}
        onTypedTextChange={() => pickupRequestRef.current.invalidate()}
      />
      {renderAddressSuggestionDropdown()}
    </CustomAddressFinderDropdownStyled>
  );
};

const CustomAddressFinderDropdownStyled = styled.div`
  position: relative;
  width: 100%;
  ${Fade};
`;
const DropdownMenu = styled(Dropdown.Menu)`
  padding: 0;
  border: none;
  box-shadow: var(--ds-bs-4);
  max-height: 160px;
  border-radius: 8px;
  width: 100%;
  overflow: auto;
  background: var(--ds-c-white);
  inset: 0px auto auto 0px !important;
  transform: translate(0px, 44px) !important;

  &::-webkit-scrollbar {
    width: 5px;
    height: 0px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--ds-c-grey-hover);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;
const DropdownItem = styled(Dropdown.Item)`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-grey-dark);

  display: flex;
  align-items: center;
  padding: 10px;
  text-transform: uppercase;
  min-height: 40px;
  height: fit-content;
  white-space: break-spaces;
  background: var(--ds-c-white);

  a:first-child {
    border-radius: 0.375rem 0.375rem 0 0;
  }

  &:hover {
    background: var(--ds-c-grey-light);
  }

  &:active {
    color: var(--ds-c-grey-dark);
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

export default CustomAddressFinderDropdown;
